import { apiClient } from '../../utils/api';
import {
  AuditLog,
  AuditFilters,
  SystemStats,
  PaginationInfo
} from '../../types/super-admin';

export interface ListAuditLogsRequest {
  page?: number;
  limit?: number;
  category?: string;
  severity?: string;
  userId?: string;
  tenantId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ListAuditLogsResponse {
  logs: AuditLog[];
  pagination: PaginationInfo;
  summary: {
    totalLogs: number;
    criticalCount: number;
    highCount: number;
    mediumCount: number;
    lowCount: number;
    categoryCounts: Record<string, number>;
  };
}

export interface ExportAuditLogsRequest {
  format: 'csv' | 'json' | 'pdf';
  filters?: AuditFilters;
  includeDetails?: boolean;
}

export interface SystemReport {
  id: string;
  type: 'usage' | 'security' | 'performance' | 'compliance';
  title: string;
  description: string;
  generatedAt: string;
  period: {
    start: string;
    end: string;
  };
  data: Record<string, any>;
  downloadUrl?: string;
}

export class AuditService {
  private static readonly BASE_PATH = '/super-admin/audit';

  static async listAuditLogs(params: ListAuditLogsRequest = {}): Promise<ListAuditLogsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${this.BASE_PATH}/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<ListAuditLogsResponse>(url);
  }

  static async getAuditLog(logId: string): Promise<AuditLog> {
    return apiClient.get<AuditLog>(`${this.BASE_PATH}/logs/${logId}`);
  }

  static async exportAuditLogs(request: ExportAuditLogsRequest): Promise<{ downloadUrl: string; jobId: string }> {
    return apiClient.post<{ downloadUrl: string; jobId: string }>(`${this.BASE_PATH}/logs/export`, request);
  }

  static async getExportStatus(jobId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    downloadUrl?: string;
    error?: string;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/export-status/${jobId}`);
  }

  static async getSystemStats(): Promise<SystemStats> {
    return apiClient.get<SystemStats>(`${this.BASE_PATH}/stats`);
  }

  static async getSystemStatsHistory(period: 'day' | 'week' | 'month', count = 30): Promise<{
    period: string;
    data: Array<{
      date: string;
      stats: SystemStats;
    }>;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/stats/history?period=${period}&count=${count}`);
  }

  static async generateReport(type: 'usage' | 'security' | 'performance' | 'compliance', options: {
    startDate: string;
    endDate: string;
    includeDetails?: boolean;
    tenantIds?: string[];
    format?: 'pdf' | 'excel' | 'json';
  }): Promise<{ reportId: string; estimatedCompletion: string }> {
    return apiClient.post<{ reportId: string; estimatedCompletion: string }>(`${this.BASE_PATH}/reports/generate`, {
      type,
      ...options
    });
  }

  static async getReportStatus(reportId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    downloadUrl?: string;
    error?: string;
    estimatedCompletion?: string;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/reports/${reportId}/status`);
  }

  static async listReports(page = 1, limit = 20): Promise<{
    reports: SystemReport[];
    pagination: PaginationInfo;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/reports?page=${page}&limit=${limit}`);
  }

  static async downloadReport(reportId: string): Promise<{ downloadUrl: string }> {
    return apiClient.get<{ downloadUrl: string }>(`${this.BASE_PATH}/reports/${reportId}/download`);
  }

  static async deleteReport(reportId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_PATH}/reports/${reportId}`);
  }

  static async getSecurityAlerts(severity?: 'critical' | 'high' | 'medium' | 'low'): Promise<{
    alerts: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      description: string;
      timestamp: string;
      resolved: boolean;
      affectedResources: string[];
      recommendedActions: string[];
    }>;
    summary: {
      total: number;
      unresolved: number;
      bySeverity: Record<string, number>;
    };
  }> {
    const url = severity 
      ? `${this.BASE_PATH}/security-alerts?severity=${severity}`
      : `${this.BASE_PATH}/security-alerts`;
    return apiClient.get<any>(url);
  }

  static async resolveSecurityAlert(alertId: string, resolution: string): Promise<void> {
    return apiClient.put<void>(`${this.BASE_PATH}/security-alerts/${alertId}/resolve`, { resolution });
  }

  static async getComplianceReport(standard: 'gdpr' | 'ccpa' | 'sox' | 'iso27001'): Promise<{
    standard: string;
    compliance: {
      score: number;
      status: 'compliant' | 'partial' | 'non-compliant';
      lastAssessment: string;
    };
    requirements: Array<{
      id: string;
      title: string;
      status: 'met' | 'partial' | 'not-met';
      evidence: string[];
      gaps: string[];
      recommendations: string[];
    }>;
    recommendations: string[];
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/compliance/${standard}`);
  }

  static async getActivitySummary(period: 'today' | 'week' | 'month'): Promise<{
    period: string;
    summary: {
      totalActions: number;
      uniqueUsers: number;
      failedLogins: number;
      dataChanges: number;
      systemAccess: number;
    };
    trends: {
      userActivity: Array<{ date: string; count: number }>;
      systemActivity: Array<{ date: string; count: number }>;
      errorRate: Array<{ date: string; rate: number }>;
    };
    topUsers: Array<{
      userId: string;
      email: string;
      actionCount: number;
    }>;
    topActions: Array<{
      action: string;
      count: number;
    }>;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/activity-summary?period=${period}`);
  }
}